/**
 * Dashboards page: main explorer (root = dashboards in top-level folders + root-level subfolders).
 * - Navigate folders via breadcrumbs and folder rows/cards (no left tree).
 * - Create folders at root and inside folders (nested).
 * - Add dashboard to folder (row menu) and Add dashboard from inside folder.
 * - Remove from folder when viewing inside a folder.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Folder as FolderIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Dashboard as DashboardIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Star as StarFilledIcon,
  StarBorder as StarOutlineIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import { useTenant } from '../../contexts/TenantContext';
import { Pagination, SearchTextField } from '../../components/common';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import ShareTargetDialog from '../../components/Dashboard/ShareTargetDialog';
import ManageSharedAccessDialog from '../../components/Dashboard/ManageSharedAccessDialog';
import SharedAccessHub from '../../components/Dashboard/SharedAccessHub';
import {
  buildScopedMyDashboardsSearchIndex,
  filterMyDashboardsSearch,
  formatSearchResultPathLabel,
} from '../../utils/myDashboardsSearch';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../../components/common';
import {
  canAccessAllDashboardsCatalog,
  isMyDashboardsOnlyAccess,
} from '../../constants/dashboardAccessPermissions';

function flattenFolders(nodes, list = []) {
  nodes.forEach((n) => {
    list.push(n);
    if (n.children?.length) flattenFolders(n.children, list);
  });
  return list;
}

function sortByName(a = '', b = '') {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

function getDashboardId(d) {
  return d.dashboardId || d.DashboardId;
}

function getDashboardName(d) {
  return d.dashboardName || d.DashboardName || d.name || 'Untitled';
}

function getWidgetCount(d) {
  const n = d.widgetCount ?? d.WidgetCount;
  return typeof n === 'number' ? n : 0;
}

function formatDate(value) {
  if (value == null) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function getCreatedMeta(item) {
  const at = item?.CreatedDate ?? item?.createdDate ?? item?.CreatedAt ?? item?.createdAt ?? null;
  const by =
    item?.CreatedByLabel ??
    item?.createdByLabel ??
    item?.CreatedBy ??
    item?.createdBy ??
    item?.CreatedByUser ??
    item?.createdByUser ??
    null;
  if (by == null || String(by).trim() === '') return { at, by: '—' };
  return { at, by: String(by).trim() };
}

/** Unique dashboards from GET /dashboards/explorer (folders + root). */
function collectAllDashboardsFromExplorer(explorerData) {
  if (!explorerData) return [];
  const seen = new Set();
  const out = [];
  const add = (d) => {
    const id = getDashboardId(d);
    if (id == null || seen.has(id)) return;
    seen.add(id);
    out.push(d);
  };
  const walk = (nodes) => {
    if (!nodes?.length) return;
    for (const f of nodes) {
      (f.dashboards || []).forEach(add);
      walk(f.children);
    }
  };
  walk(explorerData.folders);
  (explorerData.rootDashboards || []).forEach(add);
  return out;
}

export default function DashboardListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTenant, hasPermission, currentUser } = useTenant();
  const userId = currentUser?.userId ?? null;
  const filterShared = searchParams.get('filter') === 'shared';
  const canAllCatalog = canAccessAllDashboardsCatalog(hasPermission);
  const myOnlyAccess = isMyDashboardsOnlyAccess(hasPermission);
  const canCreateDashboard = hasPermission('create_dashboard');
  /** URL: ?scope=my; users with only access_my_dashboards are forced to my (API + UI). */
  const catalogListScope = searchParams.get('scope') === 'my' ? 'my' : 'all';
  const effectiveCatalogScope = myOnlyAccess ? 'my' : catalogListScope;
  const rootExplorerLabel = effectiveCatalogScope === 'my' ? 'My Dashboards' : 'All Dashboards';
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const PAGE_SIZE = 12;
  /** From GET /dashboards/explorer — nested folders with dashboards; rootDashboards = not in any folder */
  const [explorerData, setExplorerData] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = root (tenant-wide dashboards + root folders)
  const [dashboardsError, setDashboardsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderParentId, setCreateFolderParentId] = useState(null);
  const [createFolderParentName, setCreateFolderParentName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [createFolderSubmitting, setCreateFolderSubmitting] = useState(false);
  const [createFolderError, setCreateFolderError] = useState(null);

  const [addToFolderDashboard, setAddToFolderDashboard] = useState(null);
  const [addToFolderDialogOpen, setAddToFolderDialogOpen] = useState(false);
  const [addToFolderSelectedIds, setAddToFolderSelectedIds] = useState([]);
  const [addToFolderSubmitting, setAddToFolderSubmitting] = useState(false);

  const [addDashboardOpen, setAddDashboardOpen] = useState(false);
  const [addDashboardSelectedIds, setAddDashboardSelectedIds] = useState([]);
  const [addDashboardSubmitting, setAddDashboardSubmitting] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDashboard, setMenuDashboard] = useState(null);

  const [folderMenuAnchor, setFolderMenuAnchor] = useState(null);
  const [folderMenuItem, setFolderMenuItem] = useState(null);
  const [folderRenameOpen, setFolderRenameOpen] = useState(false);
  const [folderRenameName, setFolderRenameName] = useState('');
  const [folderRenameSubmitting, setFolderRenameSubmitting] = useState(false);
  const [folderRenameError, setFolderRenameError] = useState(null);
  const [folderMoveOpen, setFolderMoveOpen] = useState(false);
  const [folderMoveTargetId, setFolderMoveTargetId] = useState(null);
  const [folderMoveSubmitting, setFolderMoveSubmitting] = useState(false);
  const [folderMoveError, setFolderMoveError] = useState(null);
  const [folderDeleteOpen, setFolderDeleteOpen] = useState(false);
  const [folderDeleteSubmitting, setFolderDeleteSubmitting] = useState(false);

  const [dashboardRenameOpen, setDashboardRenameOpen] = useState(false);
  const [dashboardRenameName, setDashboardRenameName] = useState('');
  const [dashboardRenameItem, setDashboardRenameItem] = useState(null);
  const [dashboardRenameSubmitting, setDashboardRenameSubmitting] = useState(false);
  const [dashboardRenameError, setDashboardRenameError] = useState(null);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (dashboardsError) {
      showErrorToast(dashboardsError);
      setDashboardsError('');
    }
  }, [dashboardsError, showErrorToast]);

  useEffect(() => {
    if (createFolderError) {
      showErrorToast(createFolderError);
      setCreateFolderError(null);
    }
  }, [createFolderError, showErrorToast]);

  useEffect(() => {
    if (folderRenameError) {
      showErrorToast(folderRenameError);
      setFolderRenameError(null);
    }
  }, [folderRenameError, showErrorToast]);

  useEffect(() => {
    if (folderMoveError) {
      showErrorToast(folderMoveError);
      setFolderMoveError(null);
    }
  }, [folderMoveError, showErrorToast]);

  useEffect(() => {
    if (dashboardRenameError) {
      showErrorToast(dashboardRenameError);
      setDashboardRenameError(null);
    }
  }, [dashboardRenameError, showErrorToast]);
  const [dashboardMoveOpen, setDashboardMoveOpen] = useState(false);
  const [dashboardMoveTargetId, setDashboardMoveTargetId] = useState(null);
  const [dashboardMoveItem, setDashboardMoveItem] = useState(null);
  const [dashboardMoveSubmitting, setDashboardMoveSubmitting] = useState(false);
  const [dashboardDeleteOpen, setDashboardDeleteOpen] = useState(false);
  const [dashboardDeleteItem, setDashboardDeleteItem] = useState(null);
  const [dashboardDeleteSubmitting, setDashboardDeleteSubmitting] = useState(false);

  const [shareDialog, setShareDialog] = useState({ open: false, mode: null, item: null });
  const [manageShareDialog, setManageShareDialog] = useState({ open: false, mode: null, item: null });
  const [listOrderBy, setListOrderBy] = useState('name'); // 'name' | 'createdAt' | 'createdBy'
  const [listOrder, setListOrder] = useState('asc');
  const { searchInput, setSearchInput, searchTerm, setSearchTerm, clearSearch } = useDebouncedSearch();
  /** 'all' | 'dashboard' | 'folder' — same idea as Users list filters */
  const [searchResultKind, setSearchResultKind] = useState('all');

  const folderTree = explorerData?.folders ?? [];
  const allFoldersFlat = useMemo(() => flattenFolders(folderTree, []), [folderTree]);
  const folderById = useMemo(() => {
    const map = new Map();
    allFoldersFlat.forEach((f) => map.set(f.FolderId, f));
    return map;
  }, [allFoldersFlat]);
  const selectedFolder = useMemo(
    () => (selectedFolderId ? folderById.get(selectedFolderId) : null),
    [folderById, selectedFolderId]
  );
  const dashboards = useMemo(() => {
    if (!explorerData) return [];
    if (!selectedFolderId) return explorerData.rootDashboards || [];
    const node = folderById.get(selectedFolderId);
    return node?.dashboards ?? [];
  }, [explorerData, selectedFolderId, folderById]);
  const allDashboards = useMemo(() => collectAllDashboardsFromExplorer(explorerData), [explorerData]);
  const myDashboardsSearchIndex = useMemo(
    () => buildScopedMyDashboardsSearchIndex(explorerData, selectedFolderId, folderById, rootExplorerLabel),
    [explorerData, selectedFolderId, folderById, rootExplorerLabel]
  );
  const searchResults = useMemo(
    () => filterMyDashboardsSearch(myDashboardsSearchIndex, searchTerm, searchResultKind),
    [myDashboardsSearchIndex, searchTerm, searchResultKind]
  );
  const immediateSubfolders = useMemo(() => {
    if (!selectedFolderId) {
      return [...folderTree].sort((a, b) => sortByName(a.FolderName || '', b.FolderName || ''));
    }
    const ch = selectedFolder?.children || [];
    return [...ch].sort((a, b) => sortByName(a.FolderName || '', b.FolderName || ''));
  }, [folderTree, selectedFolderId, selectedFolder]);
  const breadcrumbSegments = useMemo(() => {
    const base = [{ id: null, label: rootExplorerLabel }];
    if (!selectedFolderId) return base;

    const chain = [];
    const seen = new Set();
    let current = folderById.get(selectedFolderId);
    while (current && !seen.has(current.FolderId)) {
      chain.push({ id: current.FolderId, label: current.FolderName || 'Folder' });
      seen.add(current.FolderId);
      current = current.ParentFolderId ? folderById.get(current.ParentFolderId) : null;
    }
    chain.reverse();
    return [...base, ...chain];
  }, [selectedFolderId, folderById, rootExplorerLabel]);
  const explorerItems = useMemo(() => {
    return [
      ...immediateSubfolders.map((f) => ({ kind: 'folder', item: f, key: `folder-${f.FolderId}` })),
      ...dashboards.map((d) => ({ kind: 'dashboard', item: d, key: `dashboard-${getDashboardId(d)}` })),
    ];
  }, [immediateSubfolders, dashboards]);

  /** Same All / Dashboards / Folders behaviour as search — applies to the explorer when not searching. */
  const filteredExplorerItems = useMemo(() => {
    if (searchResultKind === 'all') return explorerItems;
    if (searchResultKind === 'folder') return explorerItems.filter((e) => e.kind === 'folder');
    return explorerItems.filter((e) => e.kind === 'dashboard');
  }, [explorerItems, searchResultKind]);

  /** Folders always above dashboards; sort columns apply within each group only. */
  const sortedExplorerItems = useMemo(() => {
    const dir = listOrder === 'asc' ? 1 : -1;
    const getSortKey = (entry) => {
      if (entry.kind === 'folder') {
        const created = getCreatedMeta(entry.item);
        return {
          name: (entry.item.FolderName || '').toLowerCase(),
          createdAt: created.at ? new Date(created.at).getTime() : null,
          createdBy: (created.by || '').toLowerCase(),
        };
      }
      const created = getCreatedMeta(entry.item);
      return {
        name: getDashboardName(entry.item).toLowerCase(),
        createdAt: created.at ? new Date(created.at).getTime() : null,
        createdBy: (created.by || '').toLowerCase(),
      };
    };
    const compare = (a, b) => {
      const ka = getSortKey(a);
      const kb = getSortKey(b);
      const key = listOrderBy === 'name' ? 'name' : listOrderBy;
      let cmp = 0;
      if (key === 'name') {
        cmp = ka.name.localeCompare(kb.name);
      } else if (key === 'createdAt') {
        const ta = ka.createdAt ?? 0;
        const tb = kb.createdAt ?? 0;
        cmp = ta - tb;
      } else {
        cmp = ka.createdBy.localeCompare(kb.createdBy);
      }
      return dir * (cmp || 0);
    };
    const folders = filteredExplorerItems.filter((e) => e.kind === 'folder');
    const dash = filteredExplorerItems.filter((e) => e.kind === 'dashboard');
    return [...folders.sort(compare), ...dash.sort(compare)];
  }, [filteredExplorerItems, listOrderBy, listOrder]);

  const totalItems = sortedExplorerItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const pagedExplorerItems = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return sortedExplorerItems.slice(startIndex, startIndex + PAGE_SIZE);
  }, [sortedExplorerItems, page, PAGE_SIZE]);

  useEffect(() => {
    setPage((prev) => Math.min(Math.max(prev, 1), totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [searchResultKind]);

  useEffect(() => {
    setPage(1);
  }, [effectiveCatalogScope]);

  useEffect(() => {
    if (!myOnlyAccess || filterShared) return;
    if (searchParams.get('scope') !== 'my') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scope', 'my');
          return next;
        },
        { replace: true }
      );
    }
  }, [myOnlyAccess, filterShared, searchParams, setSearchParams]);

  useEffect(() => {
    setSearchInput('');
    setSearchTerm('');
    setSearchResultKind('all');
  }, [selectedFolderId, setSearchInput, setSearchTerm]);

  const goToDashboard = (item) => {
    const id = item.jobId || getDashboardId(item);
    const q = new URLSearchParams();
    if (selectedFolderId) q.set('folderId', selectedFolderId);
    if (effectiveCatalogScope === 'my') q.set('scope', 'my');
    const qs = q.toString();
    navigate(`/dashboard/${id}${qs ? `?${qs}` : ''}`);
  };

  const handleCatalogScopeChange = useCallback(
    (_, v) => {
      if (v == null) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === 'my') next.set('scope', 'my');
          else next.delete('scope');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const openExplorerFolder = useCallback(
    (folderId) => {
      setSelectedFolderId(folderId);
      const next = new URLSearchParams(searchParams);
      if (folderId) next.set('folderId', folderId);
      else next.delete('folderId');
      if (effectiveCatalogScope === 'my') next.set('scope', 'my');
      else next.delete('scope');
      navigate(`/dashboards?${next.toString()}`);
    },
    [navigate, searchParams, effectiveCatalogScope]
  );

  const handleSearchResultNavigate = useCallback(
    (row) => {
      clearSearch();
      if (row.kind === 'folder' && row.folderItem?.FolderId) {
        openExplorerFolder(row.folderItem.FolderId);
        return;
      }
      if (row.kind === 'dashboard' && row.dashboardItem) {
        goToDashboard(row.dashboardItem);
      }
    },
    [clearSearch, goToDashboard, openExplorerFolder]
  );

  const handleListSort = (column) => {
    const isAsc = listOrderBy === column && listOrder === 'asc';
    setListOrder(isAsc ? 'desc' : 'asc');
    setListOrderBy(column);
  };

  const loadExplorer = useCallback(async () => {
    if (!currentTenant?.tenantId) return;
    setLoading(true);
    setDashboardsError('');
    try {
      const res = await apiRef.current.get('/dashboards/explorer', {
        headers: { 'X-Tenant-ID': currentTenant.tenantId },
        params: { scope: effectiveCatalogScope },
      });
      const data = res.data?.data;
      setExplorerData({
        folders: Array.isArray(data?.folders) ? data.folders : [],
        rootDashboards: Array.isArray(data?.rootDashboards) ? data.rootDashboards : [],
      });
    } catch (e) {
      console.error('Load explorer failed:', e);
      setExplorerData({ folders: [], rootDashboards: [] });
      setDashboardsError(getApiErrorMessage(e, 'Could not load dashboards.'));
    } finally {
      setLoading(false);
    }
  }, [currentTenant?.tenantId, effectiveCatalogScope]);

  useEffect(() => {
    if (filterShared) {
      setExplorerData(null);
      return undefined;
    }
    loadExplorer();
    return undefined;
  }, [filterShared, loadExplorer]);

  const folderFromUrl = searchParams.get('folder');
  useEffect(() => {
    if (filterShared || !folderFromUrl) return;
    setSelectedFolderId(folderFromUrl);
  }, [folderFromUrl, filterShared]);

  useEffect(() => {
    const fid = searchParams.get('folderId');
    if (!fid) return;
    if (!allFoldersFlat.length) return;
    const ok = allFoldersFlat.some((f) => f.FolderId === fid);
    if (ok) setSelectedFolderId(fid);
  }, [searchParams, allFoldersFlat]);

  const handleCreateFolder = (parentId = null, parentName = '') => {
    if (!canCreateDashboard) return;
    setCreateFolderError(null);
    setCreateFolderParentId(parentId);
    setCreateFolderParentName(parentName);
    setNewFolderName('');
    setCreateFolderOpen(true);
  };

  const handleCreateFolderSubmit = async () => {
    if (!canCreateDashboard || !newFolderName.trim() || !userId) return;
    setCreateFolderSubmitting(true);
    setCreateFolderError(null);
    try {
      await api.post('/folder', {
        FolderName: newFolderName.trim(),
        CreatedBy: userId,
        ...(createFolderParentId && { ParentFolderId: createFolderParentId }),
      });
      setCreateFolderOpen(false);
      loadExplorer();
    } catch (e) {
      const msg = getApiErrorMessage(e, 'Failed to create folder.');
      setCreateFolderError(msg);
    } finally {
      setCreateFolderSubmitting(false);
    }
  };

  const handleOpenDashboardRowMenu = (dashboard, event) => {
    setMenuAnchor(event.currentTarget);
    setMenuDashboard(dashboard);
  };

  const handleAddToFolderOpen = () => {
    if (!canCreateDashboard) return;
    setMenuAnchor(null);
    if (!menuDashboard) return;
    setAddToFolderDashboard(menuDashboard);
    setAddToFolderSelectedIds([]);
    setAddToFolderDialogOpen(true);
  };

  const handleAddToFolderSubmit = async () => {
    if (!canCreateDashboard) return;
    const dashboardId = addToFolderDashboard && getDashboardId(addToFolderDashboard);
    if (!dashboardId || addToFolderSelectedIds.length === 0) return;
    setAddToFolderSubmitting(true);
    try {
      await api.post(`/dashboards/${dashboardId}/folders`, {
        folderIds: addToFolderSelectedIds,
      });
      setAddToFolderDialogOpen(false);
      loadExplorer();
    } catch (e) {
      console.error('Add to folder failed:', e);
    } finally {
      setAddToFolderSubmitting(false);
    }
  };

  const handleRemoveFromFolder = async (dashboard) => {
    const id = getDashboardId(dashboard);
    if (!selectedFolderId || !id) return;
    try {
      await api.delete(`/dashboards/${id}/folders/${selectedFolderId}`);
      setDashboardsError('');
      loadExplorer();
    } catch (e) {
      console.error('Remove from folder failed:', e);
      const msg = getApiErrorMessage(e, 'Could not remove dashboard from this folder.');
      setDashboardsError(msg);
    }
  };

  const handleToggleFavorite = async (dashboard, e) => {
    e.stopPropagation();
    const id = dashboard.dashboardId || dashboard.DashboardId;
    if (!id) return;
    try {
      const res = await api.put(`/dashboards/${id}/favorite`);
      if (res.data?.success) {
        await loadExplorer();
      }
    } catch (err) {
      console.error('Toggle favorite failed:', err);
    }
  };

  const handleTogglePublic = async (dashboard, e) => {
    e?.stopPropagation?.();
    const id = dashboard.dashboardId || dashboard.DashboardId;
    if (!id) return;
    const name = dashboard.dashboardName || dashboard.DashboardName || dashboard.name || 'Untitled';
    const description = dashboard.description ?? dashboard.Description ?? '';
    const isPublic = !(dashboard.isPublic ?? dashboard.IsPublic ?? false);
    try {
      await api.put(`/dashboards/${id}`, {
        dashboardName: name,
        description,
        isPublic,
      });
      loadExplorer();
    } catch (err) {
      console.error('Toggle public failed:', err);
    }
  };

  const handleAddDashboardOpen = () => {
    if (!canCreateDashboard) return;
    setAddDashboardSelectedIds([]);
    setAddDashboardOpen(true);
  };

  const handleAddDashboardSubmit = async () => {
    if (!canCreateDashboard || !selectedFolderId || addDashboardSelectedIds.length === 0) return;
    setAddDashboardSubmitting(true);
    try {
      for (const id of addDashboardSelectedIds) {
        await api.post(`/dashboards/${id}/folders`, { folderIds: [selectedFolderId] });
      }
      setAddDashboardOpen(false);
      loadExplorer();
    } catch (e) {
      console.error('Add dashboard to folder failed:', e);
    } finally {
      setAddDashboardSubmitting(false);
    }
  };

  const toggleAddToFolderSelection = (folderId) => {
    setAddToFolderSelectedIds((prev) =>
      prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]
    );
  };

  const toggleAddDashboardSelection = (dashboardId) => {
    setAddDashboardSelectedIds((prev) =>
      prev.includes(dashboardId) ? prev.filter((id) => id !== dashboardId) : [...prev, dashboardId]
    );
  };

  const handleFolderMenuOpen = (folder, e) => {
    e.stopPropagation();
    setFolderMenuAnchor(e.currentTarget);
    setFolderMenuItem(folder);
  };

  const handleFolderMenuClose = () => {
    setFolderMenuAnchor(null);
    setFolderMenuItem(null);
  };

  const handleFolderRenameOpen = () => {
    const currentFolder = folderMenuItem;
    handleFolderMenuClose();
    if (!currentFolder) return;
    setFolderMenuItem(currentFolder);
    setFolderRenameName(currentFolder.FolderName || '');
    setFolderRenameError(null);
    setFolderRenameOpen(true);
  };

  const handleFolderRenameSubmit = async () => {
    if (!folderMenuItem?.FolderId || !folderRenameName.trim() || !userId) return;
    setFolderRenameSubmitting(true);
    setFolderRenameError(null);
    try {
      await api.put('/folder', {
        FolderId: folderMenuItem.FolderId,
        FolderName: folderRenameName.trim(),
        ModifiedBy: userId,
      });
      setFolderRenameOpen(false);
      loadExplorer();
    } catch (e) {
      setFolderRenameError(getApiErrorMessage(e, 'Failed to rename folder.'));
    } finally {
      setFolderRenameSubmitting(false);
    }
  };

  const handleFolderMoveOpen = () => {
    const currentFolder = folderMenuItem;
    handleFolderMenuClose();
    if (!currentFolder) return;
    setFolderMenuItem(currentFolder);
    setFolderMoveTargetId(null);
    setFolderMoveError(null);
    setFolderMoveOpen(true);
  };

  const handleFolderMoveSubmit = async () => {
    if (!folderMenuItem?.FolderId || !userId) return;
    setFolderMoveSubmitting(true);
    setFolderMoveError(null);
    try {
      await api.post('/folder/move', {
        FolderId: folderMenuItem.FolderId,
        ...(folderMoveTargetId ? { NewParentFolderId: folderMoveTargetId } : {}),
        ModifiedBy: userId,
      });
      setFolderMoveOpen(false);
      loadExplorer();
      if (selectedFolderId === folderMenuItem.FolderId) openExplorerFolder(null);
    } catch (e) {
      setFolderMoveError(getApiErrorMessage(e, 'Failed to move folder.'));
    } finally {
      setFolderMoveSubmitting(false);
    }
  };

  const handleFolderDeleteOpen = () => {
    const currentFolder = folderMenuItem;
    handleFolderMenuClose();
    if (!currentFolder) return;
    setFolderMenuItem(currentFolder);
    setFolderDeleteOpen(true);
  };

  const openShareFolderFromMenu = () => {
    const folder = folderMenuItem;
    handleFolderMenuClose();
    if (folder) setShareDialog({ open: true, mode: 'folder', item: folder });
  };

  const openShareDashboardFromMenu = () => {
    const dashboard = menuDashboard;
    setMenuAnchor(null);
    if (dashboard) setShareDialog({ open: true, mode: 'dashboard', item: dashboard });
  };

  const openManageShareFolderFromMenu = () => {
    const folder = folderMenuItem;
    handleFolderMenuClose();
    if (folder) setManageShareDialog({ open: true, mode: 'folder', item: folder });
  };

  const openManageShareDashboardFromMenu = () => {
    const dashboard = menuDashboard;
    setMenuAnchor(null);
    if (dashboard) setManageShareDialog({ open: true, mode: 'dashboard', item: dashboard });
  };

  const handleFolderDeleteConfirm = async () => {
    if (!folderMenuItem?.FolderId || !userId) return;
    setFolderDeleteSubmitting(true);
    try {
      await api.delete('/folder', {
        data: { FolderId: folderMenuItem.FolderId, DeletedBy: userId, Cascade: true },
      });
      setFolderDeleteOpen(false);
      loadExplorer();
      if (selectedFolderId === folderMenuItem.FolderId) openExplorerFolder(null);
    } catch (e) {
      console.error('Delete folder failed:', e);
    } finally {
      setFolderDeleteSubmitting(false);
    }
  };

  const handleDashboardRenameOpen = () => {
    setMenuAnchor(null);
    if (!menuDashboard) return;
    setDashboardRenameItem(menuDashboard);
    setDashboardRenameName(getDashboardName(menuDashboard));
    setDashboardRenameError(null);
    setDashboardRenameOpen(true);
  };

  const handleDashboardRenameSubmit = async () => {
    const id = dashboardRenameItem && getDashboardId(dashboardRenameItem);
    if (!id || !dashboardRenameName.trim()) return;
    setDashboardRenameSubmitting(true);
    setDashboardRenameError(null);
    try {
      await api.put(`/dashboards/${id}`, { dashboardName: dashboardRenameName.trim() });
      setDashboardRenameOpen(false);
      loadExplorer();
    } catch (e) {
      setDashboardRenameError(getApiErrorMessage(e, 'Failed to rename dashboard.'));
    } finally {
      setDashboardRenameSubmitting(false);
    }
  };

  const handleDashboardMoveOpen = () => {
    setMenuAnchor(null);
    if (!menuDashboard) return;
    setDashboardMoveItem(menuDashboard);
    setDashboardMoveTargetId(null);
    setDashboardMoveOpen(true);
  };

  const handleDashboardMoveSubmit = async () => {
    const id = dashboardMoveItem && getDashboardId(dashboardMoveItem);
    if (!id || !dashboardMoveTargetId) return;
    setDashboardMoveSubmitting(true);
    try {
      await api.post(`/dashboards/${id}/folders`, { folderIds: [dashboardMoveTargetId] });
      if (selectedFolderId) {
        await api.delete(`/dashboards/${id}/folders/${selectedFolderId}`);
      }
      setDashboardMoveOpen(false);
      loadExplorer();
    } catch (e) {
      console.error('Move dashboard failed:', e);
    } finally {
      setDashboardMoveSubmitting(false);
    }
  };

  const handleDashboardDeleteOpen = () => {
    setMenuAnchor(null);
    if (!menuDashboard) return;
    setDashboardDeleteItem(menuDashboard);
    setDashboardDeleteOpen(true);
  };

  const handleDashboardDeleteConfirm = async () => {
    const id = dashboardDeleteItem && getDashboardId(dashboardDeleteItem);
    if (!id) return;
    setDashboardDeleteSubmitting(true);
    try {
      await api.delete(`/dashboards/${id}`);
      setDashboardDeleteOpen(false);
      loadExplorer();
    } catch (e) {
      console.error('Delete dashboard failed:', e);
    } finally {
      setDashboardDeleteSubmitting(false);
    }
  };

  const folderMovePickerOptions = useMemo(() => {
    if (!folderMenuItem) return [];
    const selfAndDescendantIds = new Set();
    const collect = (nodes) => {
      nodes.forEach((n) => {
        selfAndDescendantIds.add(n.FolderId);
        if (n.children?.length) collect(n.children);
      });
    };
    const findAndCollect = (nodes, targetId) => {
      for (const n of nodes) {
        if (n.FolderId === targetId) {
          collect([n]);
          return true;
        }
        if (n.children?.length && findAndCollect(n.children, targetId)) return true;
      }
      return false;
    };
    findAndCollect(folderTree, folderMenuItem.FolderId);
    return allFoldersFlat.filter((f) => !selfAndDescendantIds.has(f.FolderId));
  }, [folderMenuItem, folderTree, allFoldersFlat]);

  return (
    <Box sx={{ minHeight: '100%' }}>
      <Box sx={{ p: 2, overflow: 'auto' }}>
        {filterShared ? (
          currentTenant?.tenantId ? (
            <SharedAccessHub tenantId={currentTenant.tenantId} />
          ) : null
        ) : (
          <>
        {selectedFolderId && (
          <Box sx={{ mb: 1 }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="folder-breadcrumb">
              {breadcrumbSegments.map((segment, index) => {
                const isLast = index === breadcrumbSegments.length - 1;
                return isLast ? (
                  <Typography key={`crumb-${segment.id ?? 'root'}`} color="text.primary" fontWeight={600}>
                    {segment.label}
                  </Typography>
                ) : (
                  <Link
                    key={`crumb-${segment.id ?? 'root'}`}
                    component="button"
                    underline="hover"
                    color="inherit"
                    onClick={() => openExplorerFolder(segment.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {segment.label}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5">
              {selectedFolderId ? `${selectedFolder?.FolderName || 'Folder'} Contents` : rootExplorerLabel}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!selectedFolderId && canCreateDashboard && (
              <Button
                variant="outlined"
                startIcon={<CreateNewFolderIcon />}
                onClick={() => handleCreateFolder(null, '')}
                size="small"
              >
                New folder
              </Button>
            )}
            {selectedFolderId && canCreateDashboard && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CreateNewFolderIcon />}
                  onClick={() => {
                    const parent = allFoldersFlat.find((f) => f.FolderId === selectedFolderId);
                    handleCreateFolder(selectedFolderId, parent?.FolderName || 'Folder');
                  }}
                  size="small"
                >
                  New subfolder
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddDashboardOpen}
                  size="small"
                >
                  Add dashboard
                </Button>
              </>
            )}
          </Box>
        </Box>
        {canAllCatalog && !filterShared && (
          <ToggleButtonGroup
            value={catalogListScope}
            exclusive
            onChange={handleCatalogScopeChange}
            size="small"
            sx={{ mb: 2, flexWrap: 'wrap' }}
            aria-label="All dashboards or my dashboards"
          >
            <ToggleButton
              value="all"
              sx={{ fontFamily: FONT_FAMILY_PRIMARY, textTransform: 'none' }}
            >
              All Dashboards
            </ToggleButton>
            <ToggleButton
              value="my"
              sx={{ fontFamily: FONT_FAMILY_PRIMARY, textTransform: 'none' }}
            >
              My Dashboards
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        <Box sx={{ width: '100%', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <SearchTextField
              placeholder={
                selectedFolderId
                  ? 'Search in this folder and subfolders…'
                  : effectiveCatalogScope === 'my'
                    ? 'Search in My Dashboards and subfolders…'
                    : 'Search in All Dashboards and subfolders…'
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onClear={clearSearch}
              aria-label="Search folders and dashboards"
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel id="dashboards-search-type-label">Show</InputLabel>
              <Select
                labelId="dashboards-search-type-label"
                label="Show"
                value={searchResultKind}
                onChange={(e) => setSearchResultKind(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="dashboard">Dashboards</MenuItem>
                <MenuItem value="folder">Folders</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : searchTerm.trim() ? (
          <Paper elevation={1} sx={{ borderRadius: '8px', width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>Created date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>Created by</TableCell>
                    <TableCell
                      padding="checkbox"
                      sx={{ width: 48, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}
                    >
                      Action
                    </TableCell>
                    <TableCell padding="none" sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                        <Typography color="text.secondary">No results match your search.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    searchResults.map((row) => {
                      if (row.kind === 'folder' && row.folderItem) {
                        const created = getCreatedMeta(row.folderItem);
                        return (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleSearchResultNavigate(row)}
                        >
                          <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FolderIcon color="action" fontSize="small" />
                                <Typography component="span" sx={{ fontSize: 'inherit' }}>
                                  {row.folderItem.FolderName}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {formatSearchResultPathLabel(row.pathLabel)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{formatDate(created.at)}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{created.by}</TableCell>
                          <TableCell padding="checkbox" />
                          <TableCell padding="none" onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleFolderMenuOpen(row.folderItem, e)}
                              aria-label="Folder actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        );
                      }
                      if (row.kind === 'dashboard' && row.dashboardItem) {
                        const item = row.dashboardItem;
                        const created = getCreatedMeta(item);
                        return (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleSearchResultNavigate(row)}
                          >
                            <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DashboardIcon color="action" fontSize="small" />
                                  <Typography component="span" sx={{ fontSize: 'inherit' }}>
                                    {getDashboardName(item)}
                                    {getWidgetCount(item) > 0 && ` (${getWidgetCount(item)})`}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                  {formatSearchResultPathLabel(row.pathLabel)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{formatDate(created.at)}</TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{created.by}</TableCell>
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Tooltip
                                title={(item.isFavorite ?? item.IsFavorite) ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleToggleFavorite(item, e)}
                                  sx={{ color: (item.isFavorite ?? item.IsFavorite) ? '#FFD700' : 'action.disabled' }}
                                >
                                  {(item.isFavorite ?? item.IsFavorite) ? (
                                    <StarFilledIcon fontSize="small" />
                                  ) : (
                                    <StarOutlineIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell padding="none" onClick={(e) => e.stopPropagation()}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenDashboardRowMenu(item, e)}
                                aria-label="Dashboard actions"
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return (
                        <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleSearchResultNavigate(row)}>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{row.displayName}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>—</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>—</TableCell>
                          <TableCell padding="checkbox" />
                          <TableCell padding="none" />
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : explorerItems.length === 0 ? (
          <Typography color="text.secondary">
            {selectedFolderId ? 'This folder is empty.' : 'No dashboards or folders yet.'}
          </Typography>
        ) : filteredExplorerItems.length === 0 ? (
          <Typography color="text.secondary">
            Nothing matches the current Show filter. Choose All, or switch to a filter that matches items in
            this folder.
          </Typography>
        ) : (
          <>
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                borderRadius: '8px',
                overflow: 'auto',
                maxWidth: '100%',
              }}
            >
              <Table
                sx={{
                  minWidth: 650,
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      <TableSortLabel
                        active={listOrderBy === 'name'}
                        direction={listOrderBy === 'name' ? listOrder : 'asc'}
                        onClick={() => handleListSort('name')}
                        sx={{ fontWeight: 600 }}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      <TableSortLabel
                        active={listOrderBy === 'createdAt'}
                        direction={listOrderBy === 'createdAt' ? listOrder : 'asc'}
                        onClick={() => handleListSort('createdAt')}
                        sx={{ fontWeight: 600 }}
                      >
                        Created date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      <TableSortLabel
                        active={listOrderBy === 'createdBy'}
                        direction={listOrderBy === 'createdBy' ? listOrder : 'asc'}
                        onClick={() => handleListSort('createdBy')}
                        sx={{ fontWeight: 600 }}
                      >
                        Created by
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      padding="checkbox"
                      sx={{ width: 48, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}
                    >
                      Action
                    </TableCell>
                    <TableCell padding="none" sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedExplorerItems.map(({ kind, item, key }) =>
                    kind === 'folder' ? (
                      (() => {
                        const created = getCreatedMeta(item);
                        return (
                      <TableRow
                        key={key}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openExplorerFolder(item.FolderId)}
                      >
                        <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FolderIcon color="action" fontSize="small" />
                            <Typography component="span" sx={{ fontSize: 'inherit' }}>
                              {item.FolderName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{formatDate(created.at)}</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{created.by}</TableCell>
                        <TableCell padding="checkbox" />
                        <TableCell padding="none" onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" onClick={(e) => handleFolderMenuOpen(item, e)} aria-label="Folder actions">
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                        );
                      })()
                    ) : (
                      (() => {
                        const created = getCreatedMeta(item);
                        return (
                          <TableRow
                            key={key}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => goToDashboard(item)}
                          >
                            <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DashboardIcon color="action" fontSize="small" />
                                <Typography component="span" sx={{ fontSize: 'inherit' }}>
                                  {getDashboardName(item)}
                                  {getWidgetCount(item) > 0 && ` (${getWidgetCount(item)})`}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{formatDate(created.at)}</TableCell>
                            <TableCell sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>{created.by}</TableCell>
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title={(item.isFavorite ?? item.IsFavorite) ? 'Remove from favorites' : 'Add to favorites'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleToggleFavorite(item, e)}
                                  sx={{ color: (item.isFavorite ?? item.IsFavorite) ? '#FFD700' : 'action.disabled' }}
                                >
                                  {(item.isFavorite ?? item.IsFavorite) ? <StarFilledIcon fontSize="small" /> : <StarOutlineIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell padding="none" onClick={(e) => e.stopPropagation()}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenDashboardRowMenu(item, e)}
                                aria-label="Dashboard actions"
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })()
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={PAGE_SIZE}
                onChange={setPage}
              />
            </Box>
          </>
        )}
          </>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {canCreateDashboard && <MenuItem onClick={handleAddToFolderOpen}>Add to folder</MenuItem>}
        {selectedFolderId && (
          <MenuItem
            onClick={() => {
              if (menuDashboard) handleRemoveFromFolder(menuDashboard);
              setMenuAnchor(null);
            }}
          >
            Remove from folder
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menuDashboard) handleTogglePublic(menuDashboard, { stopPropagation: () => {} });
            setMenuAnchor(null);
          }}
        >
          {menuDashboard && (menuDashboard.isPublic ?? menuDashboard.IsPublic) ? 'Make private' : 'Make public'}
        </MenuItem>
        {hasPermission('share_dashboard') && (
          <MenuItem onClick={openShareDashboardFromMenu}>Share dashboard</MenuItem>
        )}
        {hasPermission('share_dashboard') && (
          <MenuItem onClick={openManageShareDashboardFromMenu}>Manage sharing</MenuItem>
        )}
        <MenuItem onClick={handleDashboardRenameOpen}>Rename</MenuItem>
        <MenuItem onClick={handleDashboardMoveOpen}>Move to folder</MenuItem>
        <MenuItem onClick={handleDashboardDeleteOpen} sx={{ color: 'error.main' }}>Delete</MenuItem>
      </Menu>

      <Menu
        anchorEl={folderMenuAnchor}
        open={Boolean(folderMenuAnchor)}
        onClose={handleFolderMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {hasPermission('share_folder') && (
          <MenuItem onClick={openShareFolderFromMenu}>Share folder</MenuItem>
        )}
        {hasPermission('share_folder') && (
          <MenuItem onClick={openManageShareFolderFromMenu}>Manage sharing</MenuItem>
        )}
        <MenuItem onClick={handleFolderRenameOpen}>Rename</MenuItem>
        <MenuItem onClick={handleFolderMoveOpen}>Move</MenuItem>
        <MenuItem onClick={handleFolderDeleteOpen} sx={{ color: 'error.main' }}>Delete</MenuItem>
      </Menu>

      <Dialog open={createFolderOpen} onClose={() => { setCreateFolderOpen(false); setCreateFolderError(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>
          {createFolderParentId ? `New subfolder in "${createFolderParentName}"` : 'New folder'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && canCreateDashboard && handleCreateFolderSubmit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFolderSubmit}
            disabled={!canCreateDashboard || !newFolderName.trim() || !userId || createFolderSubmitting}
            variant="contained"
          >
            {createFolderSubmitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addToFolderDialogOpen}
        onClose={() => setAddToFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add to folder</DialogTitle>
        <DialogContent>
          <List dense>
            {allFoldersFlat.map((f) => (
              <ListItemButton
                key={f.FolderId}
                onClick={() => toggleAddToFolderSelection(f.FolderId)}
                selected={addToFolderSelectedIds.includes(f.FolderId)}
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary={f.FolderName} secondary={f.FolderPath} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToFolderDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddToFolderSubmit}
            disabled={!canCreateDashboard || addToFolderSelectedIds.length === 0 || addToFolderSubmitting}
            variant="contained"
          >
            {addToFolderSubmitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDashboardOpen} onClose={() => setAddDashboardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add dashboard to this folder</DialogTitle>
        <DialogContent>
          <List dense>
            {allDashboards
              .filter((d) => !dashboards.some((x) => getDashboardId(x) === getDashboardId(d)))
              .map((d) => (
                <ListItemButton
                  key={getDashboardId(d)}
                  onClick={() => toggleAddDashboardSelection(getDashboardId(d))}
                  selected={addDashboardSelectedIds.includes(getDashboardId(d))}
                >
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary={getDashboardName(d)} />
                </ListItemButton>
              ))}
          </List>
          {allDashboards.length === 0 && !loading && (
            <Typography color="text.secondary">No other dashboards to add.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDashboardOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddDashboardSubmit}
            disabled={!canCreateDashboard || addDashboardSelectedIds.length === 0 || addDashboardSubmitting}
            variant="contained"
          >
            {addDashboardSubmitting ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={folderRenameOpen} onClose={() => setFolderRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            fullWidth
            value={folderRenameName}
            onChange={(e) => setFolderRenameName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFolderRenameSubmit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleFolderRenameSubmit}
            disabled={!folderRenameName.trim() || folderRenameSubmitting || !userId}
          >
            {folderRenameSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={folderMoveOpen} onClose={() => setFolderMoveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Move folder</DialogTitle>
        <DialogContent>
          <List dense>
            <ListItemButton
              selected={!folderMoveTargetId}
              onClick={() => setFolderMoveTargetId(null)}
            >
              <ListItemIcon>
                <FolderIcon />
              </ListItemIcon>
              <ListItemText primary={rootExplorerLabel} secondary="Move to top level" />
            </ListItemButton>
            {folderMovePickerOptions.map((f) => (
              <ListItemButton
                key={f.FolderId}
                selected={folderMoveTargetId === f.FolderId}
                onClick={() => setFolderMoveTargetId(f.FolderId)}
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary={f.FolderName} secondary={f.FolderPath} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderMoveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleFolderMoveSubmit}
            disabled={folderMoveSubmitting || !userId}
          >
            {folderMoveSubmitting ? 'Moving…' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={folderDeleteOpen} onClose={() => setFolderDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete folder</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently remove this folder, all subfolders, and dashboards inside them.
          </Alert>
          <Typography variant="body2">
            Are you sure you want to delete &quot;{folderMenuItem?.FolderName || 'this folder'}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleFolderDeleteConfirm}
            disabled={folderDeleteSubmitting || !userId}
          >
            {folderDeleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dashboardRenameOpen} onClose={() => setDashboardRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename dashboard</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Dashboard name"
            fullWidth
            value={dashboardRenameName}
            onChange={(e) => setDashboardRenameName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleDashboardRenameSubmit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDashboardRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDashboardRenameSubmit}
            disabled={!dashboardRenameName.trim() || dashboardRenameSubmitting}
          >
            {dashboardRenameSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dashboardMoveOpen} onClose={() => setDashboardMoveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Move dashboard to folder</DialogTitle>
        <DialogContent>
          <List dense>
            {allFoldersFlat.map((f) => (
              <ListItemButton
                key={f.FolderId}
                selected={dashboardMoveTargetId === f.FolderId}
                onClick={() => setDashboardMoveTargetId(f.FolderId)}
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary={f.FolderName} secondary={f.FolderPath} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDashboardMoveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDashboardMoveSubmit}
            disabled={!dashboardMoveTargetId || dashboardMoveSubmitting}
          >
            {dashboardMoveSubmitting ? 'Moving…' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dashboardDeleteOpen} onClose={() => setDashboardDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete dashboard</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete &quot;{dashboardDeleteItem ? getDashboardName(dashboardDeleteItem) : 'this dashboard'}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDashboardDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDashboardDeleteConfirm}
            disabled={dashboardDeleteSubmitting}
          >
            {dashboardDeleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <ManageSharedAccessDialog
        open={manageShareDialog.open}
        onClose={() => setManageShareDialog({ open: false, mode: null, item: null })}
        onSharesChanged={loadExplorer}
        resourceType={manageShareDialog.mode === 'folder' ? 'folder' : 'dashboard'}
        resourceId={
          manageShareDialog.mode === 'folder'
            ? manageShareDialog.item?.FolderId
            : manageShareDialog.item
              ? getDashboardId(manageShareDialog.item)
              : null
        }
        resourceLabel={
          manageShareDialog.mode === 'folder'
            ? manageShareDialog.item?.FolderName
            : manageShareDialog.item
              ? getDashboardName(manageShareDialog.item)
              : ''
        }
        tenantId={currentTenant?.tenantId}
        canManage={
          manageShareDialog.mode === 'folder'
            ? hasPermission('share_folder')
            : hasPermission('share_dashboard')
        }
      />

      <ShareTargetDialog
        open={shareDialog.open}
        onClose={(ok) => {
          setShareDialog({ open: false, mode: null, item: null });
          if (ok) loadExplorer();
        }}
        resourceType={shareDialog.mode || 'dashboard'}
        resourceId={
          shareDialog.mode === 'folder'
            ? shareDialog.item?.FolderId
            : shareDialog.item
              ? getDashboardId(shareDialog.item)
              : null
        }
        resourceLabel={
          shareDialog.mode === 'folder'
            ? shareDialog.item?.FolderName
            : shareDialog.item
              ? getDashboardName(shareDialog.item)
              : ''
        }
        currentUserId={userId}
        tenantId={currentTenant?.tenantId}
      />
    </Box>
  );
}
